export const generateDocuments = (type, answers) => {
  switch (type) {
    case 'partnership':
      return generatePartnershipDocuments(answers);
    case 'contractor':
      return generateContractorDocuments(answers);
    case 'employment':
      return generateEmploymentDocuments(answers);
    default:
      return [];
  }
};

const generatePartnershipDocuments = (answers) => {
  const documents = [];
  
  // Main Partnership Agreement
  documents.push({
    title: 'Founders Partnership Agreement',
    description: 'Primary contract between co-founders covering equity, roles, and governance',
    content: generatePartnershipAgreement(answers)
  });

  // NDA
  documents.push({
    title: 'Confidentiality Agreement (NDA)',
    description: 'Non-disclosure agreement protecting shared business information',
    content: generateNDA(answers, 'partnership')
  });

  // Vesting Agreement (if applicable)
  if (answers.vesting_schedule !== 'none') {
    documents.push({
      title: 'Equity Vesting Agreement',
      description: 'Details of equity vesting schedule and conditions',
      content: generateVestingAgreement(answers)
    });
  }

  return documents;
};

const generateContractorDocuments = (answers) => {
  const documents = [];
  
  // Main Contractor Agreement
  documents.push({
    title: 'Independent Contractor Agreement',
    description: 'Service contract between client and contractor',
    content: generateContractorAgreement(answers)
  });

  // NDA (if required)
  if (answers.confidentiality === 'yes') {
    documents.push({
      title: 'Non-Disclosure Agreement (NDA)',
      description: 'Confidentiality agreement for sensitive information',
      content: generateNDA(answers, 'contractor')
    });
  }

  // Payment Schedule (if milestone-based)
  if (answers.compensation_structure === 'milestone') {
    documents.push({
      title: 'Payment Schedule',
      description: 'Detailed milestone and payment breakdown',
      content: generatePaymentSchedule(answers)
    });
  }

  return documents;
};

const generateEmploymentDocuments = (answers) => {
  const documents = [];
  
  // Offer Letter
  documents.push({
    title: 'Employment Offer Letter',
    description: 'Formal job offer with key terms and conditions',
    content: generateOfferLetter(answers)
  });

  // Employment Agreement
  documents.push({
    title: 'Employment Agreement',
    description: 'Comprehensive employment contract with detailed terms',
    content: generateEmploymentAgreement(answers)
  });

  // NDA and IP Assignment (if required)
  if (answers.confidentiality_ip === 'yes') {
    documents.push({
      title: 'Confidentiality and IP Assignment Agreement',
      description: 'Employee confidentiality and intellectual property assignment',
      content: generateEmployeeNDA(answers)
    });
  }

  return documents;
};

const generatePartnershipAgreement = (answers) => {
  const currentDate = new Date().toLocaleDateString();
  
  return `FOUNDERS PARTNERSHIP AGREEMENT

This Founders Partnership Agreement ("Agreement") is entered into on ${currentDate} between the parties listed below (collectively, the "Founders"):

${answers.party_details || 'Founder details to be filled'}

RECITALS

WHEREAS, the Founders wish to establish a business partnership for the following venture:
${answers.business_details || 'Business details to be filled'}

NOW, THEREFORE, the parties agree as follows:

1. PARTNERSHIP FORMATION
The Founders hereby form a partnership for the purpose of conducting the business described above.

2. OWNERSHIP AND EQUITY
The ownership interests of the Founders shall be as follows:
${answers.equity_split || 'Equity split to be defined'}

3. CAPITAL CONTRIBUTIONS
Initial capital contributions by each Founder:
${answers.initial_capital || 'No initial capital contributions specified'}

4. ROLES AND RESPONSIBILITIES
Each Founder's primary responsibilities:
${answers.roles_responsibilities || 'Roles and responsibilities to be defined'}

5. COMPENSATION
${answers.compensation === 'none' ? 'The Founders agree to work without salary initially until the partnership achieves profitability or secures funding.' :
  answers.compensation === 'equal' ? 'All Founders will receive equal compensation as determined by mutual agreement.' :
  'Founder compensation will vary based on role and contribution as mutually agreed.'}

6. DECISION MAKING
${answers.decision_making === 'unanimous' ? 'All major business decisions require unanimous consent of all Founders.' :
  answers.decision_making === 'majority' ? 'Business decisions will be made by majority vote of the Founders.' :
  'Voting rights are proportional to each Founder\'s equity ownership percentage.'}

7. VESTING SCHEDULE
${answers.vesting_schedule === 'standard' ? 'Founder equity shall vest over four (4) years with a one (1) year cliff. If a Founder leaves before the cliff, all equity is forfeited. After the cliff, equity vests monthly.' :
  answers.vesting_schedule === 'none' ? 'No vesting schedule applies; all equity is immediately owned by each Founder.' :
  'Custom vesting schedule as separately agreed by the Founders.'}

8. FOUNDER DEPARTURE
${answers.exit_clauses || 'Departure terms to be defined based on circumstances (voluntary vs. involuntary) and vesting status.'}

9. INTELLECTUAL PROPERTY
${answers.ip_ownership === 'company' ? 'All intellectual property created by Founders in connection with the business shall be owned by the partnership/company.' :
  answers.ip_ownership === 'joint' ? 'Intellectual property shall be jointly owned by all Founders.' :
  'Intellectual property ownership terms to be separately defined.'}

10. DISSOLUTION AND SALE
${answers.dissolution === 'unanimous' ? 'Dissolution of the partnership or sale of the business requires unanimous consent of all Founders.' :
  answers.dissolution === 'supermajority' ? 'Dissolution or sale requires approval by at least 75% of Founders by ownership percentage.' :
  'Dissolution or sale requires majority approval of Founders.'}

11. ADDITIONAL TERMS
${answers.additional_terms || 'No additional terms specified.'}

12. GOVERNING LAW
This Agreement shall be governed by the laws of [STATE/JURISDICTION].

13. DISPUTE RESOLUTION
Any disputes arising under this Agreement shall be resolved through binding arbitration.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the date first written above.

[Signature blocks for each Founder]

---

DISCLAIMER: This document is generated based on your inputs and common legal practices. 
It is recommended to have this agreement reviewed by a qualified attorney before execution, 
especially for complex arrangements or significant financial commitments.`;
};

const generateContractorAgreement = (answers) => {
  const currentDate = new Date().toLocaleDateString();
  
  return `INDEPENDENT CONTRACTOR AGREEMENT

This Independent Contractor Agreement ("Agreement") is entered into on ${currentDate} between:

${answers.parties_info || 'Party information to be filled'}

RECITALS

WHEREAS, Client desires to engage Contractor to provide certain services; and
WHEREAS, Contractor agrees to provide such services as an independent contractor;

NOW, THEREFORE, the parties agree as follows:

1. INDEPENDENT CONTRACTOR RELATIONSHIP
Contractor is an independent contractor and not an employee of Client. Contractor is responsible for all taxes, insurance, and benefits. This Agreement does not create an employment relationship, partnership, or joint venture.

2. SCOPE OF WORK
Contractor agrees to provide the following services:

${answers.scope_of_work || 'Scope of work to be defined'}

3. PROJECT TIMELINE
${answers.timeline || 'Timeline to be established'}

4. COMPENSATION
${answers.compensation_structure === 'fixed' ? 'Contractor will be paid a fixed fee for the entire project as detailed in the payment terms below.' :
  answers.compensation_structure === 'hourly' ? 'Contractor will be paid on an hourly basis as detailed in the payment terms below.' :
  answers.compensation_structure === 'milestone' ? 'Contractor will be paid based on completion of specific milestones as detailed in the payment schedule.' :
  'Contractor will be paid on a monthly retainer basis as detailed below.'}

Payment Terms:
${answers.payment_terms || 'Payment terms to be specified'}

5. EXPENSES
${answers.expenses === 'none' ? 'Contractor is responsible for all expenses incurred in performing the services.' :
  answers.expenses === 'preapproved' ? 'Client will reimburse only pre-approved expenses with proper documentation.' :
  'Expense reimbursement terms as separately agreed.'}

6. CONFIDENTIALITY
${answers.confidentiality === 'yes' ? 'Contractor agrees to maintain strict confidentiality of all Client information and may be required to sign a separate NDA.' :
  'Contractor agrees to maintain professional discretion regarding Client information.'}

7. INTELLECTUAL PROPERTY
${answers.ip_rights === 'client' ? 'All work product, deliverables, and intellectual property created under this Agreement shall be owned exclusively by Client as "work made for hire."' :
  answers.ip_rights === 'contractor' ? 'Contractor retains ownership of all work product and grants Client a license to use such work for the intended purposes.' :
  'Intellectual property ownership to be separately defined.'}

8. TERMINATION
${answers.termination || 'Either party may terminate this Agreement with written notice. Upon termination, Contractor will be paid for all work completed to date.'}

9. WARRANTIES AND INDEMNIFICATION
Contractor warrants that all work will be original and will not infringe on third-party rights. Contractor agrees to indemnify Client against any claims arising from breach of this warranty.

10. GOVERNING LAW AND DISPUTES
This Agreement shall be governed by [STATE] law. Disputes shall be resolved through binding arbitration.

11. ADDITIONAL TERMS
${answers.additional_contractor_terms || 'No additional terms specified.'}

IN WITNESS WHEREOF, the parties have executed this Agreement as of the date first written above.

CLIENT:                          CONTRACTOR:

_____________________           _____________________
Signature                       Signature

_____________________           _____________________
Print Name                      Print Name

_____________________           _____________________
Date                           Date

---

DISCLAIMER: This document is generated based on your inputs. Please consult with a qualified 
attorney to ensure compliance with applicable laws and regulations in your jurisdiction.`;
};

const generateEmploymentAgreement = (answers) => {
  const currentDate = new Date().toLocaleDateString();
  
  return `EMPLOYMENT AGREEMENT

This Employment Agreement ("Agreement") is entered into on ${currentDate} between:

${answers.employer_employee_info || 'Employer and employee information to be filled'}

1. POSITION AND DUTIES
${answers.job_details || 'Job details and responsibilities to be specified'}

2. COMPENSATION AND BENEFITS
${answers.compensation_details || 'Compensation package to be detailed'}

3. EMPLOYMENT TYPE AND SCHEDULE
${answers.employment_type === 'fulltime_permanent' ? 'This is full-time permanent employment, typically 40+ hours per week.' :
  answers.employment_type === 'parttime_permanent' ? 'This is part-time permanent employment with reduced hours as specified.' :
  'This is contract employment for a specified term as detailed above.'}

4. AT-WILL EMPLOYMENT
${answers.at_will_employment === 'yes' ? 'This is at-will employment. Either party may terminate the employment relationship at any time, with or without cause or notice, subject to applicable law.' :
  'This employment is subject to the specific term and conditions outlined in this agreement.'}

5. BENEFITS
${answers.benefits_package || 'Benefits package as detailed in company policies'}

6. PROBATIONARY PERIOD
${answers.probationary_period === 'none' ? 'There is no probationary period. Full benefits and policies apply from the start date.' :
  answers.probationary_period === '90_days' ? 'The first 90 days of employment constitute a probationary period during which certain benefits may not apply and termination may occur with reduced notice.' :
  'Probationary period terms as specified above.'}

7. CONFIDENTIALITY AND INTELLECTUAL PROPERTY
${answers.confidentiality_ip === 'yes' ? 'Employee agrees to sign separate confidentiality and intellectual property assignment agreements as a condition of employment.' :
  'Standard confidentiality obligations apply to protect company information.'}

8. POST-EMPLOYMENT RESTRICTIONS
${answers.non_compete_terms === 'none' ? 'No post-employment restrictions apply beyond standard confidentiality obligations.' :
  answers.non_compete_terms === 'non_solicit' ? 'Employee agrees not to solicit company employees or customers for a period after termination.' :
  'Post-employment restrictions as detailed in separate agreement.'}

9. TERMINATION AND SEVERANCE
${answers.termination_severance || 'Termination terms and any severance provisions as specified'}

10. GENERAL PROVISIONS
- This Agreement shall be governed by [STATE] law
- Any disputes shall be resolved through binding arbitration
- This Agreement constitutes the entire agreement between the parties
- Modifications must be in writing and signed by both parties

11. ADDITIONAL TERMS
${answers.additional_employment_terms || 'No additional terms specified.'}

By signing below, both parties acknowledge they have read, understood, and agree to be bound by this Agreement.

EMPLOYER:                        EMPLOYEE:

_____________________           _____________________
Signature                       Signature

_____________________           _____________________
Print Name and Title            Print Name

_____________________           _____________________
Date                           Date

---

DISCLAIMER: This employment agreement is generated based on your inputs and general legal practices. 
Employment law varies significantly by jurisdiction. Please consult with an employment attorney 
to ensure compliance with federal, state, and local employment laws.`;
};

const generateOfferLetter = (answers) => {
  const currentDate = new Date().toLocaleDateString();
  
  return `EMPLOYMENT OFFER LETTER

${currentDate}

Dear [Employee Name],

We are pleased to offer you the position of [Job Title] with [Company Name]. We believe your skills and experience will be a valuable addition to our team.

POSITION DETAILS:
${answers.job_details || 'Position details from job description'}

COMPENSATION:
${answers.compensation_details || 'Compensation package details'}

EMPLOYMENT TYPE:
${answers.employment_type === 'fulltime_permanent' ? 'This is a full-time permanent position.' :
  answers.employment_type === 'parttime_permanent' ? 'This is a part-time permanent position.' :
  'This is a contract position for the specified term.'}

AT-WILL EMPLOYMENT:
${answers.at_will_employment === 'yes' ? 'Please note that this is an at-will employment relationship, which means that either you or the company may terminate the employment relationship at any time, with or without cause or advance notice.' : 'Employment terms as specified in the detailed employment agreement.'}

BENEFITS:
You will be eligible for the following benefits:
${answers.benefits_package || 'Benefits package as outlined in company policies'}

PROBATIONARY PERIOD:
${answers.probationary_period === 'none' ? 'There is no probationary period for this position.' :
  'This position includes a probationary period as detailed in the employment agreement.'}

CONDITIONS:
This offer is contingent upon:
- Successful completion of background check
- Verification of eligibility to work in the United States
- Signing of employment agreement and confidentiality agreements
- [Any other specific conditions]

ACCEPTANCE:
To accept this offer, please sign and return this letter by [Date - typically 1 week from offer date]. Your anticipated start date is [Start Date from form].

We look forward to welcoming you to our team! If you have any questions about this offer, please don't hesitate to contact me.

Sincerely,

[Hiring Manager Name]
[Title]
[Company Name]
[Contact Information]

---

ACCEPTANCE:
I accept the terms of this employment offer.

Employee Signature: _________________________ Date: _________

Print Name: _________________________________

---

DISCLAIMER: This offer letter is generated based on your inputs. Please ensure all terms 
comply with applicable employment laws and company policies before extending to candidates.`;
};

const generateNDA = (answers, type) => {
  const currentDate = new Date().toLocaleDateString();
  
  return `NON-DISCLOSURE AGREEMENT

This Non-Disclosure Agreement ("Agreement") is entered into on ${currentDate} between the parties identified below:

${type === 'partnership' ? answers.party_details : 
  type === 'contractor' ? answers.parties_info : 
  answers.employer_employee_info || 'Party information'}

1. DEFINITION OF CONFIDENTIAL INFORMATION
"Confidential Information" includes all non-public, proprietary, or confidential information disclosed by any party, including but not limited to:
- Business plans, strategies, and financial information
- Customer lists, supplier information, and business relationships
- Technical data, trade secrets, and proprietary processes
- Product information, research, and development plans
- Marketing strategies and competitive analysis
- Any other information marked or identified as confidential

2. OBLIGATIONS OF RECEIVING PARTY
Each party agrees to:
- Hold all Confidential Information in strict confidence
- Not disclose Confidential Information to third parties without written consent
- Use Confidential Information solely for the purposes of this business relationship
- Take reasonable precautions to protect the confidentiality of all information
- Return or destroy all Confidential Information upon request

3. EXCEPTIONS
This Agreement does not apply to information that:
- Is publicly available through no breach of this Agreement
- Was known to the receiving party prior to disclosure
- Is independently developed without use of Confidential Information
- Is required to be disclosed by law or court order

4. TERM
This Agreement shall remain in effect for a period of [3-5 years] from the date of execution, or until the information is no longer confidential, whichever is longer.

5. REMEDIES
The parties acknowledge that breach of this Agreement would cause irreparable harm for which monetary damages would be inadequate. Therefore, injunctive relief and other equitable remedies are appropriate.

6. GOVERNING LA W
This Agreement shall be governed by the laws of [STATE/JURISDICTION].

IN WITNESS WHEREOF, the parties have executed this Agreement as of the date first written above.

[Signature blocks for all parties]

---

DISCLAIMER: This NDA template is provided for general informational purposes. 
Consult with a qualified attorney for specific legal advice and to ensure 
compliance with applicable laws in your jurisdiction.`;
};

const generateVestingAgreement = (answers) => {
  return `EQUITY VESTING AGREEMENT

This Equity Vesting Agreement supplements the Founders Partnership Agreement and establishes the vesting schedule for founder equity.

VESTING TERMS:
${answers.vesting_schedule === 'standard' ? 
`- Vesting Period: Four (4) years from the commencement date
- Vesting Cliff: One (1) year cliff period
- Vesting Schedule: Monthly vesting after the cliff period
- Acceleration: Partial acceleration may apply in certain circumstances` :
'Custom vesting terms as agreed by the founders'}

DEPARTURE PROVISIONS:
${answers.exit_clauses || 'Standard departure and forfeiture provisions apply'}

This vesting schedule is designed to align founder interests with long-term company success and protect all parties in case of early departure.

[Additional vesting terms and conditions]

[Signature blocks]`;
};

const generatePaymentSchedule = (answers) => {
  return `PAYMENT SCHEDULE

This Payment Schedule is attached to and forms part of the Independent Contractor Agreement.

PROJECT: [Project Name]
TOTAL CONTRACT VALUE: [Total Amount]

MILESTONE-BASED PAYMENTS:
${answers.payment_terms || 'Payment milestones and amounts to be detailed based on project scope'}

INVOICE REQUIREMENTS:
- Invoices must include milestone completion evidence
- Net payment terms as specified in main agreement
- All invoices should reference this contract and milestone

PAYMENT TERMS:
- Payments due within specified timeframe after milestone acceptance
- Late payment penalties may apply as per main agreement
- Final payment contingent on project completion and acceptance

This schedule ensures payments are tied to deliverable completion and project progress.

[Signature blocks]`;
};

const generateEmployeeNDA = (answers) => {
  const currentDate = new Date().toLocaleDateString();
  
  return `EMPLOYEE CONFIDENTIALITY AND INTELLECTUAL PROPERTY ASSIGNMENT AGREEMENT

This Agreement is entered into on ${currentDate} between [Company Name] and [Employee Name].

1. CONFIDENTIALITY OBLIGATIONS
Employee agrees to maintain strict confidentiality of all company information, including but not limited to customer data, business strategies, financial information, and proprietary processes.

2. INTELLECTUAL PROPERTY ASSIGNMENT
All work product, inventions, and intellectual property created by Employee during employment shall be owned exclusively by the Company as "work made for hire."

3. NON-SOLICITATION
${answers.non_compete_terms !== 'none' ? 
'Employee agrees not to solicit company employees or customers for a specified period after termination.' :
'Standard professional conduct expectations apply.'}

4. RETURN OF PROPERTY
Upon termination, Employee agrees to return all company property, including documents, equipment, and confidential information.

5. TERM AND SURVIVAL
These obligations survive termination of employment and remain in effect as specified.

This agreement protects company interests while respecting employee rights.

[Signature blocks]

---

DISCLAIMER: Employment law varies by jurisdiction. Consult with an employment attorney 
to ensure this agreement complies with applicable federal, state, and local laws.`;
};